<template>
    <card class="card-pricing bg-gradient-default border-0 text-center mb-4"
        header-classes="bg-transparent"
        body-classes="px-lg-7"
        footer-classes="bg-transparent"
        v-if="simulation">

        <simulation-info :registration="registration" :simulation="simulation"></simulation-info>

        <div class="display-3 text-white mb-2">
            <span class="text-muted d-inline">{{ registration.degree.name }} /</span>
            {{ simulation.name }}
        </div>

        <div class="mb-3">
            <a class="mr-2" href="#" @click.prevent="openPrevious" v-if="previous">
                <i class="fas fa-angle-double-left"></i> {{ previous.name }}
            </a>
            <a class="ml-2" href="#" @click.prevent="openNext" v-if="next">
                {{ next.name }} <i class="fas fa-angle-double-right"></i>
            </a>
        </div>

        <div class="mb-4" v-if="$permissions.hasRight('dashboard.learners.write')">
            <a class="mr-2" href="#" @click.prevent="openPreviousReview" v-if="previousReview">
                <i class="fas fa-angle-double-left"></i> {{ previousReview.name }}
            </a>
            <a class="mx-2" href="#" @click.prevent="openOverview">
                {{ $t('dashboard.degree-overview') }}
            </a>
            <a class="ml-2" href="#" @click.prevent="openNextReview" v-if="nextReview">
                {{ nextReview.name }} <i class="fas fa-angle-double-right"></i>
            </a>
        </div>

        <div class="row mb-4" v-if="simulation.session">
            <div class="col-md-6 text-center pb-2">
                <span class="icon icon-xs icon-shape bg-white shadow rounded-circle">
                    <i class="fas fa-calendar-alt"></i>
                </span>
                <span class="pl-2 text-sm text-white">
                    {{ sessionDate }}
                </span>
            </div>
            <div class="col-md-6 text-center pb-2">
                <span class="icon icon-xs icon-shape bg-white shadow rounded-circle">
                    <i class="fas fa-calendar-alt"></i>
                </span>
                <span class="pl-2 text-sm text-white">
                    <span class="text-muted">{{ $t('common.from') }}</span> {{ sessionFrom }}
                    <span class="text-muted">{{ $t('common.to').toLowerCase() }}</span> {{ sessionTo }}
                </span>
            </div>
        </div>

        <div class="row mb-4" v-if="!simulation.session && simulation.state.published">
            <div class="col-12 text-center pb-2">
                <span class="icon icon-xs icon-shape bg-white shadow rounded-circle">
                    <i class="fas fa-calendar-alt"></i>
                </span>
                <span class="pl-2 text-sm text-white">
                    {{ simulationDate }}
                </span>
            </div>
        </div>

        <div class="mb-2" v-if="!simulation.assessment">

            <base-button type="warning" @click="openSessionLog"
                v-if="myActiveSession && !simulation.session.ended_at && $permissions.hasRight('dashboard.learners.write')"
            >
                <i class="fas fa-play mr-1"></i> {{ $t('common.log') }}
            </base-button>

            <base-button type="secondary" @click="openSessionLog"
                v-if="myActiveSession && simulation.session.ended_at && $permissions.hasRight('dashboard.learners.write')"
            >
                <i class="fas fa-play mr-1"></i> {{ $t('common.log') }}
            </base-button>

            <base-button type="secondary" @click="openLog"
                v-if="!myActiveSession && !simulation.remedial && $permissions.hasRight('dashboard.passive-log.access')"
            >
                {{ $t('common.log') }}
            </base-button>

            <base-button type="secondary" @click="openComment"
                :disabled="!simulation.state.comment"
                v-if="simulation.state.published"
            >
                {{ $t('dashboard.general-comment') }}
            </base-button>

            <base-button type="secondary" @click="editComment"
                v-if="!simulation.state.published && allowCommentEdit && $permissions.hasRight('dashboard.learners.write')"
            >
                <i class="fas fa-pencil-alt mr-1"></i> {{ $t('dashboard.general-comment') }}
            </base-button>
        </div>

        <div class="mb-2" v-if="simulation.assessment && simulation.private">

            <base-button type="secondary" @click="editComment"
                v-if="!simulation.state.published && allowCommentEdit && $permissions.hasRight('dashboard.learners.write')"
            >
                <i class="fas fa-pencil-alt mr-1"></i> {{ $t('dashboard.general-comment') }}
            </base-button>
        </div>

        <portal to="modals">

            <comment-modal v-model="modals.comment" :comment="comment">
            </comment-modal>
            
            <comment-edit-modal v-model="modals.editComment" :comment="comment"
                :endpoint="commentEndpoint"
                @change="$emit('change')"
            >
            </comment-edit-modal>
            
        </portal>
    </card>
</template>

<script>
    import ModalsController from '@trax/classes/ModalsController'
    import SimulationInfo from '../common/SimulationInfo'
    import CommentModal from './CommentModal'
    import CommentEditModal from './CommentEditModal'
    import moment from 'moment'

    export default {

        components: {
            CommentModal, CommentEditModal, SimulationInfo
        },

        props: {

            simulation: {
                type: Object,
            },

            simulations: {
                type: Array,
            },

            registration: {
                type: Object,
            },

            allowCommentEdit: {
                type: Boolean,
                default: false
            },
        },

        data() {
            return {
                modals: new ModalsController(['comment', 'editComment']),
                moment: moment,
            }
        },

        computed: {

            comment() {
                return { 
                    title: this.simulation.name,
                    content: this.simulation.state.comment,
                    author: this.simulation.state.instructor_name,
                }
            },

            commentEndpoint() {
                return '/astral/api/front/dashboard/registrations/'+this.$route.params.registrationId
                    +'/simulations/'+this.$route.params.simulationId
                    +'/learners/'+this.$route.params.learnerId
                    +'/comment'
            },

            myActiveSession() {
                return this.simulation.session
                    && !this.simulation.state.published
                    && this.simulation.session.instructor.id == this.$auth.user.id
            },

            simulationDate() {
                return this.moment(this.simulation.state.date).format('YYYY/MM/DD')
            },

            sessionDate() {
                let date = this.simulation.session.started_at ? this.simulation.session.started_at : this.simulation.session.created_at
                return this.moment(date).format('YYYY/MM/DD')
            },
            
            sessionFrom() {
                if (!this.simulation.session.started_at) {
                    return '-'
                }
                return this.moment(this.simulation.session.started_at).format('HH:MM')
            },
            
            sessionTo() {
                if (!this.simulation.session.ended_at) {
                    return '-'
                }
                return this.moment(this.simulation.session.ended_at).format('HH:MM')
            },
            
            currentIndex() {
                return this.simulations.findIndex(simulation => simulation.id == this.$route.params.simulationId)
            },

            previous() {
                let index = this.currentIndex
                while (index > 0) {
                    index--
                    if (!this.simulations[index].private || this.$permissions.hasRight('dashboard.learners.write')) {
                        return this.simulations[index]
                    }
                }
                return null
            },

            next() {
                let index = this.currentIndex
                while (index < this.simulations.length - 1) {
                    index++
                    if (!this.simulations[index].private || this.$permissions.hasRight('dashboard.learners.write')) {
                        return this.simulations[index]
                    }
                }
                return null
            },

            previousReview() {
                let index = this.currentIndex
                while (index > 0) {
                    index--
                    if (this.simulations[index].private && this.$permissions.hasRight('dashboard.learners.write')) {
                        return this.simulations[index]
                    }
                }
                return null
            },

            nextReview() {
                let index = this.currentIndex
                while (index < this.simulations.length - 1) {
                    index++
                    if (this.simulations[index].private && this.$permissions.hasRight('dashboard.learners.write')) {
                        return this.simulations[index]
                    }
                }
                return null
            },
        },

        methods: {

            openLog() {
                this.$router.push({
                    name: 'log-learner',
                    params: {
                        learnerId: this.$route.params.learnerId,
                        registrationId: this.$route.params.registrationId,
                        simulationId: this.$route.params.simulationId
                    }
                });
            },

            openSessionLog() {
                this.$router.push({ name: 'log-session',
                    params: { 
                        sessionId: this.simulation.session.id,
                        registrationId: this.registration.id,
                        simulationId: this.simulation.id,
                        groupId: 0,
                    }
                })
            },

            openComment() {
                this.modals.open('comment')
            },

            editComment() {
                if (!this.simulation.state.published && this.allowCommentEdit && this.$permissions.hasRight('dashboard.learners.write')) {
                    this.modals.open('editComment')
                }
            },

            openPrevious() {
                let name = this.previous.assessment && this.previous.private
                    ? 'dashboard-learner-private-review-report'
                    : 'dashboard-learner-simulation'

                this.$router.push({
                    name: name,
                    params: {
                        learnerId: this.$route.params.learnerId,
                        registrationId: this.$route.params.registrationId,
                        simulationId: this.previous.id,
                    }
                });
            },


            openNext() {
                let name = this.next.assessment && this.next.private
                    ? 'dashboard-learner-private-review-report'
                    : 'dashboard-learner-simulation'

                this.$router.push({
                    name: name,
                    params: {
                        learnerId: this.$route.params.learnerId,
                        registrationId: this.$route.params.registrationId,
                        simulationId: this.next.id,
                    }
                });
            },

            openPreviousReview() {
                this.$router.push({
                    name: 'dashboard-learner-private-review-report',
                    params: {
                        learnerId: this.$route.params.learnerId,
                        registrationId: this.$route.params.registrationId,
                        simulationId: this.previousReview.id,
                    }
                });
            },


            openNextReview() {
                this.$router.push({
                    name: 'dashboard-learner-private-review-report',
                    params: {
                        learnerId: this.$route.params.learnerId,
                        registrationId: this.$route.params.registrationId,
                        simulationId: this.nextReview.id,
                    }
                });
            },

            openOverview() {
                this.$router.push({
                    name: 'dashboard-learner-registration-overview',
                    params: {
                        learnerId: this.$route.params.learnerId,
                        registrationId: this.$route.params.registrationId,
                    }
                });
            },
        }
    }
</script>

<style scoped>

    .card-pricing >>> a {
        color: #a1aef7;
    }
    .card-pricing >>> a:hover {
        color: #c9d1fb;
    }
    
</style>